import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useNavigate } from "react-router-dom";
import { ThemeColor } from "../../component/theme/color";
import { FontFamily } from "../../component/theme/fontFamily";
import { apiErrorMessage } from "../../helper/apiError";
import {
  getPayoutProgressByScopeAPI,
} from "../../Redux/service/accountService";
import {
  QontoConnector,
  QontoStepIcon,
  activeStepOf,
  formatStageDate,
} from "./trackingStation";

/**
 * The seven steps for one university or organisation.
 *
 * The same station the per-student view draws, counted across everyone here:
 * which steps have happened, when each was last run, and how many students are
 * still waiting on it.
 */
export const TrackingStation = ({
  stages,
  notApproved = 0,
  approved = 0,
  // A university is asked how many commissions it approved; an organisation is
  // asked how many were refused. Same station, different question.
  showApproved = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const steps = (stages || []).map((stage) => ({
    ...stage,
    done: Boolean(stage.lastAt) || stage.done > 0,
  }));
  const completed = steps.filter((step) => step.done).length;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <Typography
          fontFamily={FontFamily.Lato}
          sx={{ fontWeight: 600, color: ThemeColor.NAVYBLUE, fontSize: 14 }}
        >
          Transaction Tracking Station
        </Typography>
        <Chip
          size="small"
          label={`${completed} of ${steps.length} stages started`}
          sx={{
            bgcolor: completed === steps.length ? "#DCFCE7" : "#F1F5F9",
            color: completed === steps.length ? "#15803D" : "#475569",
            fontWeight: 600,
            fontSize: "0.7rem",
            borderRadius: "6px",
          }}
        />
        {showApproved && approved > 0 ? (
          <Chip
            size="small"
            label={`${approved} approved`}
            sx={{
              bgcolor: "#DCFCE7",
              color: "#15803D",
              fontWeight: 600,
              fontSize: "0.7rem",
              borderRadius: "6px",
            }}
          />
        ) : null}
        {!showApproved && notApproved > 0 ? (
          <Chip
            size="small"
            label={`${notApproved} not approved`}
            sx={{
              bgcolor: "#FEE2E2",
              color: "#B91C1C",
              fontWeight: 600,
              fontSize: "0.7rem",
              borderRadius: "6px",
            }}
          />
        ) : null}
      </Box>

      <Box sx={{ width: "100%", mt: 2, px: { xs: 1, md: 3 } }}>
        <Stepper
          alternativeLabel={!isMobile}
          orientation={isMobile ? "vertical" : "horizontal"}
          activeStep={activeStepOf(steps)}
          connector={<QontoConnector />}
        >
          {steps.map((step) => (
            <Step key={step.key} completed={step.done}>
              <StepLabel StepIconComponent={QontoStepIcon}>
                <Typography
                  sx={{
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: step.done ? ThemeColor.NAVYBLUE : "#94A3B8",
                  }}
                >
                  {step.label}
                </Typography>

                {step.lastAt ? (
                  <Typography sx={{ fontSize: 11, color: "#15803D", mt: 0.25 }}>
                    Last: {formatStageDate(step.lastAt)}
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: 11, color: "#94A3B8", mt: 0.25 }}>
                    Pending
                  </Typography>
                )}

                {step.count > 0 ? (
                  <Typography sx={{ fontSize: 11, color: "#B45309", mt: 0.25 }}>
                    {step.count} waiting here
                  </Typography>
                ) : null}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
    </Box>
  );
};

/** One expanded row: the station by default, and a link to the full history. */
const EntityDetail = ({ scope, row, intake, university, universityName }) => {
  const navigate = useNavigate();

  const handleViewAllHistory = () => {
    const params = new URLSearchParams();
    if (scope === "organisation") {
      params.set("organizationId", row.id);
      // An organisation's record is always for one university.
      if (university) params.set("university", university);
      if (universityName) params.set("universityName", universityName);
    } else {
      params.set("universityId", row.id);
    }
    if (row.name) {
      params.set("entityName", row.name);
    }
    // Opening the full record keeps whatever semester was being looked at.
    // Landing on every intake meant finding your place again, and the page
    // has its own filter for widening back out.
    if (intake) {
      params.set("intake", intake);
    }
    navigate(`/CAE/accounts/payout-history?${params.toString()}`);
  };

  return (
    <Box sx={{ px: 3, py: 2.5, backgroundColor: "#F8FAFC" }}>
      <TrackingStation
        stages={row.stages}
        notApproved={row.notApproved}
        approved={row.approved}
        showApproved={scope !== "organisation"}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
        <Typography
          onClick={handleViewAllHistory}
          sx={{
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
            color: ThemeColor.PURPLE,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          View all history
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Progress for every university or organisation.
 *
 * The student view answers "where is this applicant". This answers "where is
 * this university", which is the question asked when chasing money.
 */
export const PayoutActivityView = ({
  scope,
  intake,
  university = "",
  universityName = "",
  // Optional: the page can own the search text and the expanded row so they
  // survive a trip to View All History and back. Left out, the view keeps
  // them itself as before.
  search: searchProp,
  onSearchChange,
  openId: openIdProp,
  onOpenChange,
}) => {
  // Organisations are read one university at a time; without one there is
  // nothing meaningful to show, so ask rather than guess.
  const needsUniversity = scope === "organisation" && !university;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [localOpenId, setLocalOpenId] = useState(null);
  const [localSearch, setLocalSearch] = useState("");

  const search = searchProp !== undefined ? searchProp : localSearch;
  const setSearch = onSearchChange || setLocalSearch;
  const openId = openIdProp !== undefined ? openIdProp : localOpenId;
  const setOpenId = onOpenChange || setLocalOpenId;

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((row) => (row.name || "").toLowerCase().includes(q));
  }, [rows, search]);

  useEffect(() => {
    if (needsUniversity) {
      setRows([]);
      setLoading(false);
      setError("");
      return undefined;
    }
    let current = true;
    setLoading(true);
    setError("");
    getPayoutProgressByScopeAPI({ scope, intake, university })
      .then((res) => {
        if (!current) return;
        setRows(res.data?.data || []);
      })
      .catch((err) => current && setError(apiErrorMessage(err)))
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
  }, [scope, intake, university, needsUniversity]);

  if (needsUniversity) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography
          fontFamily={FontFamily.Lato}
          sx={{ fontWeight: 600, color: ThemeColor.NAVYBLUE, fontSize: 15 }}
        >
          Select a university to see its organisations
        </Typography>
        <Typography sx={{ color: ThemeColor.GREY, fontSize: 13, mt: 0.5 }}>
          An organisation is exported to, invoiced and paid one university at a
          time, so its progress is shown per university.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography sx={{ color: ThemeColor.RED, p: 3 }}>{error}</Typography>
    );
  }

  if (!rows.length) {
    return (
      <Typography sx={{ color: ThemeColor.GREY, p: 3 }}>
        No {scope === "organisation" ? "organisations" : "universities"} with
        payouts for this filter.
      </Typography>
    );
  }

  const entityLabel =
    scope === "organisation" ? "organisations" : "universities";

  return (
    <Box>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          borderBottom: "1px solid #E2E8F0",
          backgroundColor: "#FFFFFF",
        }}
      >
        <TextField
          size="small"
          placeholder={
            scope === "organisation"
              ? "Search organisations..."
              : "Search universities..."
          }
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#94A3B8", fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            minWidth: { xs: "100%", sm: 300 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: "#F8FAFC",
            },
          }}
        />

        <Typography
          fontFamily={FontFamily.Lato}
          fontSize="0.825rem"
          color="#64748B"
          fontWeight={600}
        >
          Showing {filteredRows.length} of {rows.length} {entityLabel}
        </Typography>
      </Box>

      {filteredRows.length === 0 ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography sx={{ color: ThemeColor.GREY, fontSize: 14 }}>
            No {entityLabel} match &ldquo;{search}&rdquo;.
          </Typography>
          <Button
            size="small"
            onClick={() => setSearch("")}
            sx={{ mt: 1, textTransform: "none" }}
          >
            Clear search
          </Button>
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#F1F5F9" }}>
                <TableCell sx={{ width: 48 }} />
                <TableCell sx={{ fontWeight: 600 }}>
                  {scope === "organisation" ? "Organisation" : "University"}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Students</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Finished</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Last action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRows.map((row) => {
            const open = openId === row.id;

            // The most recent thing that happened anywhere in this row.
            const latest = (row.stages || [])
              .filter((stage) => stage.lastAt)
              .sort((a, b) => (a.lastAt < b.lastAt ? 1 : -1))[0];

            return (
              <React.Fragment key={row.id}>
                <TableRow hover>
                  <TableCell>
                    <IconButton
                      size="small"
                      aria-label={open ? "Hide details" : "Show details"}
                      onClick={() => setOpenId(open ? null : row.id)}
                    >
                      {open ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                  <TableCell>{row.students}</TableCell>
                  <TableCell>
                    {row.completed} of {row.students}
                  </TableCell>
                  <TableCell>
                    {latest ? (
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                          {latest.label}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 11.5, color: ThemeColor.GREY }}
                        >
                          {formatStageDate(latest.lastAt)}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography
                        sx={{ fontSize: 12.5, color: ThemeColor.GREY }}
                      >
                        Not started
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                      <EntityDetail
                        scope={scope}
                        row={row}
                        intake={intake}
                        university={university}
                        universityName={universityName}
                      />
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
      )}
    </Box>
  );
};

export default PayoutActivityView;
